import { Button, Group, Stack, Text } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';

import type { FounderLetter } from '../../components/marketing/FounderLetterCard';
import { FounderLetterCard } from '../../components/marketing/FounderLetterCard';
import { MarketingHero } from '../../components/marketing/MarketingHero';
import { MarketingNextSteps } from '../../components/marketing/MarketingNextSteps';
import { MarketingSection } from '../../components/marketing/MarketingSection';
import { PublicShell } from '../../components/marketing/PublicShell';
import { JoinLink } from '../../components/ui/JoinLink';
import { useCmsImage, useCmsSlot } from '../../lib/cms';

interface ApiLetter {
  slug: string;
  label: string;
  title: string;
  excerpt: string;
  body: string;
  author: string;
  published_at: string;
}

function fromApi(row: ApiLetter): FounderLetter {
  return {
    slug: row.slug,
    label: row.label,
    title: row.title,
    excerpt: row.excerpt,
    body: row.body,
    author: row.author,
    publishedAt: row.published_at,
  };
}

export const Route = createFileRoute('/ripples/')({
  loader: async () => {
    const res = await fetch('/api/__public__/notes-from-the-road');
    if (!res.ok) return { letters: [] as FounderLetter[] };
    const data = (await res.json()) as { items: ApiLetter[] };
    return { letters: data.items.map(fromApi) };
  },
  component: NotesIndexPage,
});

function NotesIndexPage() {
  const { letters } = Route.useLoaderData();
  const { image: heroImg, ready: heroImgReady } = useCmsImage('notes_from_the_road.hero_image');
  const heroEyebrow = useCmsSlot<'markdown'>('notes_from_the_road.hero_eyebrow');
  const heroTitle = useCmsSlot<'markdown'>('notes_from_the_road.hero_title');
  const heroLede = useCmsSlot<'markdown'>('notes_from_the_road.hero_lede');
  const archiveEyebrow = useCmsSlot<'markdown'>('notes_from_the_road.archive_eyebrow');
  const archiveTitleEmpty = useCmsSlot<'markdown'>('notes_from_the_road.archive_title_empty');
  const archiveTitleActive = useCmsSlot<'markdown'>('notes_from_the_road.archive_title_active');
  const archiveLedeEmpty = useCmsSlot<'markdown'>('notes_from_the_road.archive_lede_empty');
  const archiveLedeActive = useCmsSlot<'markdown'>('notes_from_the_road.archive_lede_active');
  const archiveEmptyBody = useCmsSlot<'markdown'>('notes_from_the_road.archive_empty_body');
  const subscribeCta = useCmsSlot<'markdown'>('notes_from_the_road.subscribe_cta');
  const nextTitle = useCmsSlot<'markdown'>('notes_from_the_road.next_title');

  return (
    <PublicShell>
      <MarketingHero
        eyebrow={heroEyebrow}
        title={heroTitle}
        lede={heroLede}
        {...(heroImgReady ? { backgroundImage: heroImg.url } : {})}
        backgroundImageAlt={heroImg.alt}
        backgroundImageOpacity={heroImg.opacity ?? 0.18}
      />

      <MarketingSection
        eyebrow={archiveEyebrow}
        title={letters.length === 0 ? archiveTitleEmpty : archiveTitleActive}
        lede={letters.length === 0 ? archiveLedeEmpty : archiveLedeActive}
      >
        {letters.length === 0 ? (
          <Stack gap="xl">
            <Text c="ink.6" size="lg" style={{ lineHeight: 1.6 }}>
              {archiveEmptyBody}
            </Text>
            {/* The early-access page owns the capture form site-wide, so this
                sends people there rather than collecting inline — the last
                on-page signup left after the /waitlist consolidation. */}
            <Group>
              <JoinLink>
                <Button
                  component="span"
                  tabIndex={-1}
                  size="lg"
                  variant="gradient"
                  gradient={{ from: 'brand.6', to: 'sun.6', deg: 35 }}
                  styles={{ root: { minWidth: 220 } }}
                >
                  {subscribeCta}
                </Button>
              </JoinLink>
            </Group>
          </Stack>
        ) : (
          <Stack gap="lg">
            {letters.map((letter) => (
              <FounderLetterCard key={letter.slug} letter={letter} />
            ))}
          </Stack>
        )}
      </MarketingSection>

      <MarketingNextSteps
        title={nextTitle}
        destinations={['/how-it-works', '/founding-venues', '/our-ethos']}
      />
    </PublicShell>
  );
}
